import axios, { AxiosInstance } from 'axios';
import { DivisionRole } from '../types';

export interface UserRegistrationRequest {
  email: string;
  firstName: string;
  lastName: string;
  divisionRoleId: string;
  organizationIdentifier: string;
}

export interface UserRegistrationResponse {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  existedInDivision: boolean;
  existedInOkta: boolean;
  addedToOktaGroup: boolean;
  workflowId: string;
  workflowName: string;
  workflowCreated: boolean;
  message: string;
}

export interface PublicDivisionConfig {
  id: string;
  name: string;
  apiKey: string;
}

export interface PublicRegistrationDivision {
  divisionId: string;
  divisionName: string;
}

export interface PublicRegistrationListItem {
  divisionId: string;
  divisionName: string;
  slug: string;
}

export class RegistrationService {
  private instance: AxiosInstance;

  constructor(baseURL: string) {
    // Create axios instance WITHOUT auth interceptors
    // This is a public service that uses API keys, not OAuth tokens
    this.instance = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  /**
   * Register a new user in a division
   * Uses the division's API key for authentication (no OAuth token required)
   */
  async registerUser(
    divisionId: string,
    apiKey: string,
    request: UserRegistrationRequest
  ): Promise<UserRegistrationResponse> {
    const response = await this.instance.post<UserRegistrationResponse>(
      `/api/v1/divisions/${divisionId}/register`,
      request,
      {
        headers: {
          'X-API-Key': apiKey,
        },
      }
    );
    return response.data;
  }

  /**
   * Get division roles for a division (public endpoint, uses API key)
   */
  async getDivisionRoles(
    divisionId: string,
    apiKey: string
  ): Promise<DivisionRole[]> {
    const response = await this.instance.get<DivisionRole[]>(
      `/api/v1/divisions/${divisionId}/public/roles`,
      {
        headers: {
          'X-API-Key': apiKey,
        },
      }
    );
    return response.data;
  }

  /**
   * Resolve a public registration link slug to its division (no API key needed - the slug itself
   * is the non-secret public id, see RegistrationController's slug-based endpoints).
   */
  async getDivisionBySlug(slug: string): Promise<PublicRegistrationDivision> {
    const response = await this.instance.get<PublicRegistrationDivision>(
      `/api/v1/public/register/${slug}`
    );
    return response.data;
  }

  /**
   * Get division roles for a public registration link slug.
   */
  async getDivisionRolesBySlug(slug: string): Promise<DivisionRole[]> {
    const response = await this.instance.get<DivisionRole[]>(
      `/api/v1/public/register/${slug}/roles`
    );
    return response.data;
  }

  /**
   * Register a new user via a public registration link slug.
   */
  async registerUserBySlug(
    slug: string,
    request: UserRegistrationRequest
  ): Promise<UserRegistrationResponse> {
    const response = await this.instance.post<UserRegistrationResponse>(
      `/api/v1/public/register/${slug}`,
      request
    );
    return response.data;
  }

  /**
   * List divisions that opted in to appear on the shared /register page.
   */
  async getPublicRegistrationDivisions(): Promise<PublicRegistrationListItem[]> {
    const response = await this.instance.get<PublicRegistrationListItem[]>(
      '/api/v1/public/registration-divisions'
    );
    return response.data;
  }

  /**
   * Get public division configuration from environment/config
   * Divisions available for public registration
   */
  getPublicDivisions(): PublicDivisionConfig[] {
    // Read from runtime config first (for container deployments), then fall back to build-time env
    const runtimeConfig = (window as any)._env_?.REACT_APP_PUBLIC_DIVISIONS;
    let publicDivisionsJson = runtimeConfig && runtimeConfig !== '__REACT_APP_PUBLIC_DIVISIONS__' 
      ? runtimeConfig 
      : process.env.REACT_APP_PUBLIC_DIVISIONS;
    
    // Check if it's base64 encoded (for container runtime config)
    if (publicDivisionsJson && publicDivisionsJson.startsWith('BASE64:')) {
      try {
        publicDivisionsJson = atob(publicDivisionsJson.substring(7));
      } catch (e) {
        console.error('Failed to decode base64 REACT_APP_PUBLIC_DIVISIONS', e);
      }
    }
    
    if (publicDivisionsJson) {
      try {
        return JSON.parse(publicDivisionsJson);
      } catch (e) {
        console.error('Failed to parse REACT_APP_PUBLIC_DIVISIONS', e);
      }
    }
    
    // Default to empty array - can be configured via environment variables
    // Format: [{"id":"guid","name":"Division Name","apiKey":"key"}]
    return [];
  }
}

// Ignore unreplaced Docker placeholder values like "__REACT_APP_API_URL__"
function isPlaceholder(value: string | undefined): value is undefined {
  return !value || (value.startsWith('__') && value.endsWith('__'));
}

// Get API base URL from runtime config (window._env_) or fallback to build-time env
function getApiBaseUrl(): string {
  if (typeof window !== 'undefined' && (window as any)._env_?.REACT_APP_API_BASE_URL) {
    const runtimeValue = (window as any)._env_.REACT_APP_API_BASE_URL;
    if (!isPlaceholder(runtimeValue)) {
      return runtimeValue;
    }
  }
  if (!isPlaceholder(process.env.REACT_APP_API_URL)) {
    return process.env.REACT_APP_API_URL as string;
  }
  if (!isPlaceholder(process.env.REACT_APP_API_BASE_URL)) {
    return process.env.REACT_APP_API_BASE_URL as string;
  }
  return 'http://localhost:5000';
}

// Create a registration service instance with runtime configuration
const API_BASE_URL = getApiBaseUrl();
export const registrationService = new RegistrationService(API_BASE_URL);
